import { useEffect, useState } from "react"

/**
 * Hook to manage stadium lights opacity based on scroll position.
 * IMPORTANT: This logic matches the original implementation EXACTLY to preserve visual appearance.
 */
export function useStadiumLightsOpacity() {
  const [opacity, setOpacity] = useState(1) // Start with lights on at top of page

  // Re-compute progress on scroll / resize
  useEffect(() => {
    function updateOpacity() {
      // Current scroll position – we sample the vertical centre of viewport so
      // the effect feels a bit earlier / smoother.
      const current = window.scrollY + window.innerHeight * 0.5
      const documentHeight = document.documentElement.scrollHeight
      const viewportHeight = window.innerHeight

      // Define the key transition points based on typical landing page layout:
      // - Sports section (Bracky) typically starts around 80-100% of first viewport
      // - Bracket section typically starts around 40-50% through the document
      const sportsStartPoint = viewportHeight * 0.9 // Start dimming when reaching sports section
      const bracketStartPoint = documentHeight * 0.45 // Start brightening back up at bracket section

      // At top of page (before sports section) - lights on full brightness
      if (current < sportsStartPoint) {
        setOpacity(1)
        return
      }

      // From bracket section onwards - ramp back up to full brightness
      if (current >= bracketStartPoint) {
        // Calculate how far we are into the bracket section
        const remainingDocument = documentHeight - bracketStartPoint
        const progressInBracket = Math.min((current - bracketStartPoint) / (remainingDocument * 0.3), 1)

        const MIN_OPACITY = 0.4
        const newOpacity = MIN_OPACITY + progressInBracket * (1 - MIN_OPACITY)
        setOpacity(newOpacity)
        return
      }

      // Between sports section and bracket section - dim to 40%
      const MIN_OPACITY = 0.4
      setOpacity(MIN_OPACITY)
    }

    updateOpacity()
    window.addEventListener("scroll", updateOpacity, { passive: true })
    window.addEventListener("resize", updateOpacity)
    return () => {
      window.removeEventListener("scroll", updateOpacity)
      window.removeEventListener("resize", updateOpacity)
    }
  }, [])

  return opacity
}
